// Custom logo trophies and corporate awards: a logo, product or mascot
// sculpted as the award itself. Targets "logo trophy" (480/mo, KD 12) and
// "corporate awards" (480/mo, $18 CPC), OpenSEO US 2026-11-03; AI answers
// reward "no minimum, made from product photos" (run a167b849). Every photo
// is real studio work already shown on /services/custom-trophy/. Prices and
// turnaround come from .seo/truth.md (custom-trophy pricing and turnaround,
// owner 2026-10-30 and 2026-11-03); nothing else is promised.
const { pricing, photos, photoUrl } = require('./custom-trophy')

const logoTrophies = {
  path: '/services/custom-trophy/logo-trophies/',
  reviewedDate: '2026-11-03',
  reviewedDateLabel: 'November 3, 2026',
  title: 'Custom Logo Trophies & Corporate Awards | PrintAWorld',
  description: `Custom logo trophies and corporate awards: your logo, product or mascot sculpted in 3D, painted or plated. From $${pricing.from} each, no minimum.`,
  answer: `PrintAWorld turns your logo, product or mascot into the trophy itself, sculpted in 3D in New York, instead of a stock cup with your logo on the plate. Send a logo file or product photos; we send a free mockup, model it, print it in-house, paint or plate it in your colors and mount it on an engraved base. Trophies start at $${pricing.from} each with no minimum. Standard turnaround is 4–6 weeks (6–8 weeks for longer, complex or plated pieces), with rush in 1–2 weeks for many projects, and we ship to all 50 states.`,
  hero: 'iHeart',
}

// Real client pieces, named the way /services/custom-trophy/ names them.
// One feature, two wide and four small fill the gallery grid; the hero
// (iHeart) isn't repeated, and the portrait Formic arm takes a small cell.
const realWork = [
  {
    photo: 'streetEasy',
    caption: 'StreetEasy logo trophies',
    layout: 'feature',
  },
  {
    photo: 'tribeGorillas',
    caption: 'Tribe sales awards as chrome gorillas',
    layout: 'wide',
  },
  {
    photo: 'rcCatsSet',
    caption: 'A team mascot as a set of awards',
    layout: 'wide',
  },
  { photo: 'formicArm', caption: 'Formic’s robot arm, as the award' },
  { photo: 'beamie', caption: 'Beamie Awards with the logo on top' },
  { photo: 'goGetter', caption: 'Dunkin’ Go Getter awards' },
  { photo: 'gcoty', caption: 'German Car of the Year, raised lettering' },
]

const ways = [
  {
    title: 'Your logo, sculpted',
    body: 'The logo becomes the trophy: modeled in 3D from your vector file, with depth you can’t get from an engraved plate, then painted in your brand colors or plated.',
  },
  {
    title: 'Your product, at desk size',
    body: 'A scale model of what you make, like the robot arm we made for Formic. Send product photos, drawings or a 3D file and we sculpt it on an engraved base.',
  },
  {
    title: 'Your mascot or character',
    body: 'A mascot, a character or an inside joke, sculpted in a pose and painted to match. One design can become a whole set, each with its own name plate.',
  },
]

const facts = [
  {
    title: 'Price',
    body: `From $${pricing.from} per trophy. Painted resin usually costs $250–$750; plated, gold or complex multi-color pieces run $750–$2,000 or more; 16 inches and taller start at $1,500.`,
  },
  {
    title: 'Design',
    body: `A free concept mockup first. A one-time 3D modeling fee of $${pricing.modelingFee} for most projects, paid once however many copies you order.`,
  },
  {
    title: 'Size',
    body: '8–12 inches tall as standard. Larger pieces are quoted with your design.',
  },
  {
    title: 'Minimum',
    body: 'None. One signature award, or a set for every winner with their own name plate.',
  },
  {
    title: 'Turnaround',
    body: '4–6 weeks standard; 6–8 weeks for longer, complex or plated pieces. Rush in 1–2 weeks is possible for many projects, depending on the design, budget and where it ships.',
  },
  {
    title: 'Delivery',
    body: 'Made in-house in New York and shipped to all 50 states, or picked up at our studio by appointment.',
  },
]

const occasions = [
  {
    title: 'Sales awards',
    body: 'Top Closer, President’s Club, quota busters, like the chrome gorilla awards we made for Tribe.',
  },
  {
    title: 'Employee recognition',
    body: 'Employee of the Year, team awards and culture awards that look like your company, not a catalog.',
  },
  {
    title: 'Hackathons and internal events',
    body: 'Winners’ trophies for a hackathon, a demo day or a company game, often with an inside joke built in.',
  },
  {
    title: 'Industry and annual awards',
    body: 'An award your organization gives every year, like German Car of the Year, with a base that fits each year’s winner.',
  },
  {
    title: 'Launches and milestones',
    body: 'A product launch, an anniversary or a big contract, marked with the product itself at desk size.',
  },
]

const steps = [
  'Send your logo (AI, SVG, EPS or PDF is best), product photos or a sketch, and tell us what the award is for.',
  'We send a free concept mockup, so you see the idea before you commit.',
  'We model the trophy in 3D and send it for your approval, with the engraving laid out.',
  'We 3D print it in-house in New York and sand it smooth.',
  'We paint it in your colors or plate it in a metal finish, then engrave the names.',
  'We mount each award on its base, pack it, and ship it or hold it for pickup.',
]

const comparison = [
  {
    title: 'A catalog award with your logo',
    body: 'A stock cup, column or acrylic shape with your logo engraved or printed on it. It costs less per piece and ships fast, and it suits a large order of identical awards on a tight budget.',
  },
  {
    title: 'A custom logo trophy',
    body: `The award is your logo, your product or your mascot, sculpted and finished for you. It starts at $${pricing.from} each and takes 4–6 weeks as standard (6–8 weeks if plated), and it’s the one people keep on their desk.`,
  },
]

const faqs = [
  {
    question: 'How much does a custom logo trophy cost?',
    answer: `Custom logo trophies start at $${pricing.from} each. Painted resin pieces usually cost $250–$750; plated, gold or complex multi-color pieces run $750–$2,000 or more, and trophies 16 inches or taller start at $1,500. There’s a one-time 3D modeling fee of $${pricing.modelingFee} for most projects, and the concept mockup is free.`,
  },
  {
    question: 'Can you make a trophy shaped like our product or logo?',
    answer:
      'Yes, that’s what we do. We sculpt your logo in 3D, or your product as a desk-size model, like the robot arm we made for Formic. Send a logo file, product photos or a 3D file, and you approve the model before anything is printed.',
  },
  {
    question: 'What logo file do you need?',
    answer:
      'A vector file (AI, SVG, EPS or PDF) works best. A high-resolution PNG is fine too, and we can redraw a logo when needed. For a product, send photos from several angles, drawings or a 3D file.',
  },
  {
    question: 'Is there a minimum order?',
    answer:
      'No. We make one signature award or a set for every winner. The 3D model is paid once, then each copy is printed, finished and engraved.',
  },
  {
    question: 'How long do custom corporate awards take?',
    answer:
      'Standard turnaround is 4–6 weeks, and 6–8 weeks for longer, complex or plated pieces (plating runs 6–8 weeks after the 3D model is approved). Rush orders in 1–2 weeks are possible for many projects, depending on how complex the design is, your budget and where it ships. Tell us your event date when you ask for a quote.',
  },
  {
    question: 'Can you match our brand colors?',
    answer:
      'We paint to match your brand colors and check the color with you before the award ships. Send your brand colors with the logo. We don’t promise an exact Pantone match on painted resin.',
  },
  {
    question: 'Are the gold and chrome finishes solid metal?',
    answer:
      'No. The trophies are printed in resin. A gold, silver or chrome look is paint or metal plating, a real metal layer over the printed piece. It looks and feels like metal, but it isn’t solid metal.',
  },
  {
    question: 'Do you make crystal or glass awards?',
    answer:
      'No. Our awards are sculpted, painted or plated pieces on solid bases. For a crystal award with a logo etched inside, a catalog awards shop is the better choice. For engraved acrylic and wood plaques, see our acrylic awards page.',
  },
  {
    question: 'Do you ship nationwide?',
    answer:
      'Yes, to all 50 states. You can also pick up at our New York studio by appointment.',
  },
]

const sendList = [
  'Your logo file, product photos or a sketch',
  'What the award is for, and how many you need',
  'Names for the plates, if you have them',
  'Your event date and delivery ZIP',
]

module.exports = {
  logoTrophies,
  photos,
  photoUrl,
  realWork,
  ways,
  facts,
  occasions,
  steps,
  comparison,
  faqs,
  sendList,
}
